import { create } from "zustand";

export type CardVerificationFailure = {
    last4: string | null;
    /**
     * removed: the API said the card was detached. kept: the check failed but the card is still on file
     * (e.g. hold_requires_action_unsupported). start_limit: too many starts, ask support.
     */
    reason: "removed" | "kept" | "start_limit";
};

/**
 * CT-301 Verify Card: the verify modal, the "verify your card first" pop-up and the fail dialog are
 * opened from many places (billing, banner, top-up, plans, numbers, the API error handler), so they
 * live in one store and CardVerificationHost draws them once in the dashboard layout.
 */
type CardVerificationState = {
    /** The card the verify modal is open for. */
    verifyingCardId: string | null;
    /** "Verify your card first": a charge was refused because the card is unverified. */
    verifyFirstOpen: boolean;
    failure: CardVerificationFailure | null;
    hostMounted: boolean;
    setHostMounted(mounted: boolean): void;
};

export const useCardVerificationStore = create<CardVerificationState>((set) => ({
    verifyingCardId: null,
    verifyFirstOpen: false,
    failure: null,
    hostMounted: false,
    setHostMounted: (hostMounted) => set({ hostMounted }),
}));

export function openCardVerification(paymentMethodId: string): void {
    useCardVerificationStore.setState({ verifyingCardId: paymentMethodId, verifyFirstOpen: false });
}

export function closeCardVerification(): void {
    useCardVerificationStore.setState({ verifyingCardId: null });
}

/** A new sign-in on this tab must not see the last user's card in an open dialog. */
export function resetCardVerification(): void {
    useCardVerificationStore.setState({ verifyingCardId: null, verifyFirstOpen: false, failure: null });
}

export function showCardVerificationFailure(failure: CardVerificationFailure): void {
    useCardVerificationStore.setState({ verifyingCardId: null, failure });
}

/** Opens "verify your card first" only where it can be drawn; false means the caller says it itself. */
export function tryOpenVerifyCardFirst(): boolean {
    const { hostMounted, verifyingCardId } = useCardVerificationStore.getState();
    if (!hostMounted) return false;
    // The verify modal is already up (a card just added); a second pop-up on top would only repeat it.
    if (verifyingCardId) return true;
    useCardVerificationStore.setState({ verifyFirstOpen: true });

    return true;
}
